<template>
    <div class="BookingParkDetail">
        <Transition
            name="fade"
            mode="out-in"
        >
            <div
                v-if="park"
                class="BookingParkDetail-container"
            >
                <PageTitle class="BookingParkDetail-title">
                    {{ $t('personal-account.park.title') }}
                </PageTitle>
                <div
                    v-if="invoice"
                    class="BookingParkDetail-invoice"
                >
                    {{ $t('personal-account.park.invoice') }} # {{ invoice.number || invoice.reference }}
                </div>

                <div class="BookingParkDetail-price">
                    {{ amount }}
                </div>
                <div class="BookingParkDetail-pricePerDay">
                    {{ pricePerDay }} / {{ $t('personal-account.park.day') }}
                </div>

                <RoundedButton
                    v-if="invoice"
                    :label="$t('personal-account.park.download-invoice')"
                    type="white"
                    class="BookingParkDetail-button"
                    @buttonClicked="goToInvoiceDetail"
                />

                <Block class="BookingParkDetail-agencyContainer">
                    <div class="BookingParkDetail-agency">
                        {{ park.from.name }}
                    </div>
                    <Rating
                        v-if="park.from?.rating"
                        :rating="park.from?.rating"
                    />
                </Block>

                <Block
                    :withoutPadding="true"
                    class="BookingParkDetail-map"
                >
                    <Map
                        :latLng="mapLatLng"
                        :markers="markers"
                        markerIcon="parking"
                    />
                    <div
                        class="BookingParkDetail-globalContainer"
                    >
                        <div
                            class="BookingParkDetail-addressContainer"
                        >
                            <div class="BookingParkDetail-dateContainer">
                                <div class="BookingParkDetail-day">
                                    {{ formatDate(park.from.date, 'DD') }}
                                </div>
                                <div class="BookingParkDetail-monthHour">
                                    <div class="BookingParkDetail-month">
                                        {{ capitalizeFirstLetter(formatDate(park.from.date, 'MMM')) }}
                                    </div>
                                    <div>
                                        {{ formatDate(park.from.date, 'H') }}:{{ formatDate(park.from.date, 'mm') }}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <span
                            class="f2m-chevron-right BookingParkDetail-chevron"
                        />
                        <div
                            class="BookingParkDetail-addressContainer"
                        >
                            <div class="BookingParkDetail-dateContainer">
                                <div class="BookingParkDetail-day">
                                    {{ formatDate(park.to.date, 'DD') }}
                                </div>
                                <div class="BookingParkDetail-monthHour">
                                    <div class="BookingParkDetail-month">
                                        {{ capitalizeFirstLetter(formatDate(park.to.date, 'MMM')) }}
                                    </div>
                                    <div>
                                        {{ formatDate(park.to.date, 'H') }}:{{ formatDate(park.to.date, 'mm') }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Block>
            </div>
        </Transition>
    </div>
</template>

<script>
import Component from 'vue-class-component'
import Vue from 'vue'
import PageTitle from '@/components/PageTitle.vue';
import Transition from "@/components/Transition.vue";
import Tag from "@/components/Tag.vue";
import Rating from "@/components/Rating.vue";

import {
    GET_INVOICES,
    GET_PARKS
} from "@/store/action-types";
import * as mutationTypes from "@/store/mutation-types";

import {RoundedButton, Block} from "@free2move/vuejs-components";
import Map from "@/components/Map.vue";

import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import Money from "@free2move/money";
dayjs.extend(localizedFormat);


@Component({
    components: {
        PageTitle,
        Transition,
        RoundedButton,
        Block,
        Map,
        Tag,
        Rating
    }
})
export default class BookingsParkDetail extends Vue {
    data() {
        return {
            invoice: null,
            park: null
        }
    }

    mounted() {
        this.$store.commit(mutationTypes.UPDATE_SHOW_TRANSPARENT_GLOBAL_LOADER, true);
        this.$store.dispatch(GET_INVOICES, {
            callback: function (dataInvoice) {
                if (dataInvoice) {
                    this.invoice = dataInvoice[0];
                }
                this.$store.dispatch(GET_PARKS, {
                    callback: function (dataPark) {
                        this.park = dataPark[0];
                        this.$store.commit(mutationTypes.UPDATE_SHOW_TRANSPARENT_GLOBAL_LOADER, false);
                        window.scrollTo(0, 0);
                    }.bind(this),
                    data: {
                        conditions: {
                            _id: this.$route.params.id,
                        }
                    }
                });

            }.bind(this),
            data: {
                conditions: {
                    modelHolder: `park+${this.$route.params.id}`,
                }
            }
        });
    }

    goToInvoiceDetail() {
        document.location.href= `/invoices/${this.invoice._id}?key=${this.invoice.key}`;
    }

    formatDate(date, format) {
        return dayjs(date).format(format)
    }

    capitalizeFirstLetter(string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    get pricePerDay() {
        return new Money({...this.park.detail.days, language: this.$language}).divide(this.park.durationInDays).format();
    }

    get amount() {
        return this.park.detail?.grandTotal ? new Money({...this.park.detail.grandTotal, language: this.$language}).format() : '';
    }

    get mapLatLng() {
        return [this.park.from.spot.latitude, this.park.from.spot.longitude]
    }

    get markers() {
        return  [{
            latLng: [this.park.from.spot.latitude, this.park.from.spot.longitude]
        }];
    }

}
</script>

<style lang="scss" scoped>
.BookingParkDetail {
    &-invoice {
        font-size: 22px;
        opacity: .7;
        color: $color-white;
    }

    &-price {
        font-weight: bold;
        font-size: 42px;
        line-height: 50px;
        color: #FFFFFF;
        margin-top: 48px;
    }

    &-pricePerDay {
        font-size: 18px;
        line-height: 22px;
        color: #FFFFFF;
        margin-bottom: 16px;
    }

    &-agencyContainer {
        margin-top: 48px;
    }

    &-agency {
        font-size: 22px;
        line-height: 26px;
    }

    &-map {
        margin-top: 24px;
    }

    &-globalContainer {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 28px;
        font-size: 14px;
        line-height: 24px;
        border-bottom: 1px solid #e6e6e6;
        padding: 28px 0;
        margin: 0 28px;
    }
    &-chevron {
        color: #467AFB;
        font-size: 24px;
    }

    &-dateContainer {
        display: flex;
        gap: 12px;
    }

    &-day {
        font-size: 42px;
        line-height: 40px;
    }

    &-monthHour {
        display: flex;
        flex-direction: column;
        color: $color-f2m-purple-icon;
        align-items: center;
        justify-content: center;
    }

    &-month {
        font-weight: bold;
        line-height: 18px;
    }
}
</style>
